import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import Box from '@mui/material/Box'
import Switch from '@/v2/components/common/Switch'
import TextField from '@/v2/components/common/TextField'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import {
  useSettingsClinicWeekHours,
  useUpdateClinicWeekHours,
} from '@/v2/hooks/settings/useSettingsClinicWeekHours'
import {
  SettingsPage,
  PrimaryButton,
  DayRow,
  DayName,
  DayToggleCell,
  DayContent,
  DayHoursLine,
  TimeRangeGroup,
  DayNote,
  TimeSeparator,
} from '@/v2/components/settings/shared'

const BreakLine = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-top: 0.5rem;
`

const BreakConnector = styled.span`
  align-self: flex-start;
  width: 16px;
  height: 28px;
  margin: -0.5rem 0.25rem 0 0.5rem;
  border-left: 1px solid #d1d5db;
  border-bottom: 1px solid #d1d5db;
  border-bottom-left-radius: 6px;
  flex-shrink: 0;
`

const BreakLabel = styled.span`
  font-size: 0.8rem;
  color: #6b7280;
  flex-shrink: 0;
`

const switchSx = { ml: 0, mr: 0 }

const DAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
]

const buildLocalState = data => {
  return DAYS.map(day => {
    const dayData = data[day] || {}
    return {
      day,
      is_open: dayData.isOpen ?? false,
      opens_at: dayData.opensAt || '',
      closes_at: dayData.closesAt || '',
      has_break: dayData.hasBreak ?? false,
      break_from: dayData.breakFrom || '',
      break_to: dayData.breakTo || '',
    }
  })
}

const buildPayload = schedule => {
  const payload = {}
  schedule.forEach(entry => {
    payload[`${entry.day}_is_open`] = entry.is_open
    payload[`${entry.day}_opens_at`] = entry.opens_at
    payload[`${entry.day}_closes_at`] = entry.closes_at
    payload[`${entry.day}_has_break`] = entry.has_break
    payload[`${entry.day}_break_from`] = entry.break_from || null
    payload[`${entry.day}_break_to`] = entry.break_to || null
  })
  return payload
}

const ClinicWeekHours = () => {
  const { weekHours, isLoading } = useSettingsClinicWeekHours()
  const { updateWeekHours, isPending: isSaving } = useUpdateClinicWeekHours()
  const { onOpenSnackbar } = useSnackbarContext()

  const [schedule, setSchedule] = useState(null)

  useEffect(() => {
    if (weekHours && Object.keys(weekHours).length > 0) {
      setSchedule(buildLocalState(weekHours))
    }
  }, [weekHours])

  const handleFieldChange = (day, field, value) => {
    setSchedule(prev =>
      prev.map(entry =>
        entry.day === day ? { ...entry, [field]: value } : entry
      )
    )
  }

  const handleToggleOpen = (day, checked) => {
    setSchedule(prev =>
      prev.map(entry =>
        entry.day === day
          ? {
              ...entry,
              is_open: checked,
              ...(checked ? {} : { has_break: false }),
            }
          : entry
      )
    )
  }

  const handleToggleBreak = (day, checked) => {
    setSchedule(prev =>
      prev.map(entry =>
        entry.day === day ? { ...entry, has_break: checked } : entry
      )
    )
  }

  const handleSave = async () => {
    if (!schedule) return

    try {
      await updateWeekHours(buildPayload(schedule))
      onOpenSnackbar('Clinic hours updated successfully', 'success')
    } catch (error) {
      onOpenSnackbar('Failed to update clinic hours', 'error')
    }
  }

  return (
    <SettingsPage
      title="Edit clinic's regular hours"
      subtitle="Set the clinic's default weekly operating hours"
      loading={isLoading}
      actions={
        <PrimaryButton onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save'}
        </PrimaryButton>
      }
    >
      {schedule && (
        <Box>
          {schedule.map(entry => (
            <DayRow key={entry.day} $alignTop="flex-start">
              <DayName $closed={!entry.is_open}>{entry.day}</DayName>

              <DayToggleCell>
                <Switch
                  label={entry.is_open ? 'Open' : 'Closed'}
                  checked={entry.is_open}
                  onChange={(e, checked) =>
                    handleToggleOpen(entry.day, checked)
                  }
                  sx={switchSx}
                />
              </DayToggleCell>

              <DayContent>
                {entry.is_open ? (
                  <>
                    <DayHoursLine>
                      <TimeRangeGroup>
                        <TextField
                          type="time"
                          value={entry.opens_at}
                          onChange={val =>
                            handleFieldChange(entry.day, 'opens_at', val)
                          }
                          size="small"
                        />
                        <TimeSeparator>to</TimeSeparator>
                        <TextField
                          type="time"
                          value={entry.closes_at}
                          onChange={val =>
                            handleFieldChange(entry.day, 'closes_at', val)
                          }
                          size="small"
                        />
                      </TimeRangeGroup>

                      <Switch
                        label="Break"
                        checked={entry.has_break}
                        onChange={(e, checked) =>
                          handleToggleBreak(entry.day, checked)
                        }
                        sx={switchSx}
                      />
                    </DayHoursLine>

                    {entry.has_break && (
                      <BreakLine>
                        <BreakConnector aria-hidden="true" />
                        <BreakLabel>Break</BreakLabel>
                        <TextField
                          type="time"
                          value={entry.break_from}
                          onChange={val =>
                            handleFieldChange(entry.day, 'break_from', val)
                          }
                          size="small"
                        />
                        <TimeSeparator>to</TimeSeparator>
                        <TextField
                          type="time"
                          value={entry.break_to}
                          onChange={val =>
                            handleFieldChange(entry.day, 'break_to', val)
                          }
                          size="small"
                        />
                      </BreakLine>
                    )}
                  </>
                ) : (
                  <DayNote>Closed all day</DayNote>
                )}
              </DayContent>
            </DayRow>
          ))}
        </Box>
      )}
    </SettingsPage>
  )
}

export default ClinicWeekHours
